import { defineStore } from 'pinia';
import { Network } from '@capacitor/network';

export type SyncState = 'online' | 'syncing' | 'pending' | 'offline' | 'conflict';

interface State {
  online: boolean;
  syncing: boolean;
  pending: number;
  conflict: boolean;
  lastSyncedAt: number | null;
}

export const useSyncStore = defineStore('sync', {
  state: (): State => ({
    online: true,
    syncing: false,
    pending: 0,
    conflict: false,
    lastSyncedAt: null,
  }),
  getters: {
    // приоритет, как в дизайне (app.js): offline > conflict > syncing > pending > online
    state(s): SyncState {
      if (!s.online) return 'offline';
      if (s.conflict) return 'conflict';
      if (s.syncing) return 'syncing';
      if (s.pending > 0) return 'pending';
      return 'online';
    },
    label(): string {
      const map: Record<SyncState, string> = {
        online: 'Синхронизировано',
        syncing: 'Синхронизация…',
        pending: `${this.pending} ${plural(this.pending)} ждут отправки`,
        offline: 'Нет соединения — изменения сохранятся и отправятся позже',
        conflict: 'Конфликт версий · Разрешить',
      };
      return map[this.state as SyncState];
    },
  },
  actions: {
    /** Подписка на сетевые события; onReconnect вызывается при возврате online. */
    async init(onReconnect: () => void) {
      try {
        const status = await Network.getStatus();
        this.online = status.connected;
      } catch {
        this.online = navigator.onLine;
      }
      Network.addListener('networkStatusChange', (status) => {
        const was = this.online;
        this.online = status.connected;
        if (!was && status.connected) onReconnect();
      });
    },
    setSyncing(v: boolean) {
      this.syncing = v;
    },
    setPending(n: number) {
      this.pending = n;
    },
    setConflict(v: boolean) {
      this.conflict = v;
    },
    markSynced() {
      this.lastSyncedAt = Date.now();
      this.conflict = false;
    },
  },
});

function plural(n: number): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return 'изменение';
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return 'изменения';
  return 'изменений';
}
